import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userService } from '../../services/userService';

interface UserRegistrationData {
  role: 'user';
  profilePicUrl?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  about?: string;
}

interface UserRegistrationResponse {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  profilePicUrl?: string;
  phoneNumber: string;
  about?: string;
  createdAt: string;
  updatedAt: string;
}

interface UserState {
  user: UserRegistrationResponse | null;
  isLoading: boolean;
  error: string | null;
  registrationSuccess: boolean;
}

const initialState: UserState = {
  user: null,
  isLoading: false,
  error: null,
  registrationSuccess: false,
};

// Async thunks
export const registerUser = createAsyncThunk(
  'user/registerUser',
  async (userData: UserRegistrationData, { rejectWithValue }) => {
    try {
      const response = await userService.registerUser(userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || 
        error?.message || 
        'User registration failed'
      );
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRegistrationSuccess: (state) => {
      state.registrationSuccess = false;
    },
    clearUser: (state) => {
      state.user = null;
      state.isLoading = false;
      state.error = null;
      state.registrationSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Register User
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.registrationSuccess = false;
      })
      .addCase(registerUser.fulfilled, (state, action: PayloadAction<UserRegistrationResponse>) => {
        state.isLoading = false;
        state.user = action.payload;
        state.registrationSuccess = true;
        state.error = null;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.registrationSuccess = false;
      });
  },
});

export const { clearError, clearRegistrationSuccess, clearUser } = userSlice.actions;
export default userSlice.reducer;
